import React, { useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Percent, ArrowLeft, Target, ArrowUpRight, ArrowDownRight, Minus, Calendar, Info } from 'lucide-react';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

const CommercialCostsAnalysis = ({ language, year, onBack, flowMap }) => {
  const [structureCompareMode, setStructureCompareMode] = useState('yoy');
  
  const yearNum = parseInt(year) || 2025;
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(yearNum, language);
  const previousPeriod = getPreviousPeriodLabel(yearNum, language);
  const budgetLabelFull = getBudgetLabel(yearNum, language);
  const titleSuffix = getTitleSuffix(yearNum, language);
  const comparisonSubtitle = getComparisonSubtitle(yearNum, language);
  
  const commercialData = mockData?.commercialCosts?.[yearNum] || {
    grossSales: 0,
    royalties: 0,
    totalSodiamFees: 0,
    totalCommercialCosts: 0,
    netRevenueAfterCharges: 0,
    takeRatePercent: 0,
    budgetVariance: 0
  };
  const prevCommercialData = mockData?.commercialCosts?.[yearNum - 1] || {};

  // Budget data for commercial costs
  const budgetCommercialCosts = commercialData.totalCommercialCosts * 0.98;
  const commercialVsBudget = budgetCommercialCosts ? ((commercialData.totalCommercialCosts - budgetCommercialCosts) / budgetCommercialCosts * 100).toFixed(1) : '0';
  const budgetTakeRate = commercialData.takeRatePercent - 0.2;
  const takeRateVsBudget = ((commercialData.takeRatePercent - budgetTakeRate) / budgetTakeRate * 100).toFixed(1);

  // Calculate Weight in Op. Costs
  const opexTotal = mockData?.opexData?.[yearNum]?.total || 120;
  const prevOpexTotal = mockData?.opexData?.[yearNum - 1]?.total || 110;
  const weightInOpex = opexTotal ? ((commercialData.totalCommercialCosts / opexTotal) * 100).toFixed(1) : '0';
  const prevWeightInOpex = prevOpexTotal && prevCommercialData.totalCommercialCosts 
    ? ((prevCommercialData.totalCommercialCosts / prevOpexTotal) * 100).toFixed(1) : '0';
  const weightInOpexChange = (parseFloat(weightInOpex) - parseFloat(prevWeightInOpex)).toFixed(1);

  // Calculate YoY changes with safety checks
  const totalCostsYoY = prevCommercialData.totalCommercialCosts && commercialData.totalCommercialCosts
    ? ((commercialData.totalCommercialCosts - prevCommercialData.totalCommercialCosts) / prevCommercialData.totalCommercialCosts * 100).toFixed(1)
    : null;
  
  const takeRateYoY = prevCommercialData.takeRatePercent && commercialData.takeRatePercent
    ? ((commercialData.takeRatePercent - prevCommercialData.takeRatePercent) / prevCommercialData.takeRatePercent * 100).toFixed(1)
    : null;

  const royaltiesYoY = prevCommercialData.royalties && commercialData.royalties
    ? ((commercialData.royalties - prevCommercialData.royalties) / prevCommercialData.royalties * 100).toFixed(1)
    : null;

  const sodiamYoY = prevCommercialData.totalSodiamFees && commercialData.totalSodiamFees
    ? ((commercialData.totalSodiamFees - prevCommercialData.totalSodiamFees) / prevCommercialData.totalSodiamFees * 100).toFixed(1)
    : null;

  // Monthly data for peak month calculations
  const monthsFull = language === 'pt' 
    ? ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  // Peak month for Royalties (simulated - September)
  const peakRoyaltiesMonth = monthsFull[8];
  const peakRoyaltiesValue = (commercialData.royalties / 12 * 1.28).toFixed(2);
  const peakRoyaltiesYoY = '+24.5';
  const peakRoyaltiesVsBudget = '+3.2';
  
  // Peak month for Sales Commission (simulated - November)
  const peakCommissionMonth = monthsFull[10];
  const peakCommissionValue = (commercialData.totalSodiamFees / 12 * 1.35).toFixed(2);
  const peakCommissionYoY = '+31.2';
  const peakCommissionVsBudget = '+5.8';

  // Cost structure data for comparison
  const costStructureData = [
    { 
      name: 'Royalties (5%)', 
      value: commercialData.royalties || 0, 
      percentage: commercialData.totalCommercialCosts ? ((commercialData.royalties / commercialData.totalCommercialCosts) * 100).toFixed(1) : '0',
      salesPercentage: 5.0,
      fill: '#0A192F',
      prevValue: prevCommercialData.royalties || 0,
      budgetValue: (commercialData.royalties || 0) * 0.97
    },
    { 
      name: language === 'pt' ? 'Comissão de Vendas' : 'Sales Commission', 
      value: commercialData.totalSodiamFees || 0, 
      percentage: commercialData.totalCommercialCosts ? ((commercialData.totalSodiamFees / commercialData.totalCommercialCosts) * 100).toFixed(1) : '0',
      salesPercentage: commercialData.grossSales ? ((commercialData.totalSodiamFees / commercialData.grossSales) * 100).toFixed(2) : '0',
      fill: '#C5A059',
      prevValue: prevCommercialData.totalSodiamFees || 0,
      budgetValue: (commercialData.totalSodiamFees || 0) * 0.96
    },
  ];

  // Key insights - same structure as CMVMC
  const insights = [
    {
      driver: { pt: 'Encargo sobre Vendas', en: 'Sales Charge' },
      detail: { 
        pt: 'Royalties e comissões: custos variáveis indexados ao valor vendido.',
        en: 'Royalties and commissions: variable costs indexed to sold value.'
      },
      direction: totalCostsYoY > 0 ? 'up' : 'down',
      impact: 'high'
    },
    {
      driver: { pt: 'Mix de Vendas', en: 'Sales Mix' },
      detail: { 
        pt: 'Evolução ligada ao peso entre pedras regulares e especiais.',
        en: 'Evolution linked to weight between regular and special stones.'
      },
      direction: 'neutral',
      impact: 'medium'
    },
    {
      driver: { pt: 'Margem Líquida', en: 'Net Margin' },
      detail: { 
        pt: 'Dedução sistemática à receita bruta, afecta margem comercial.',
        en: 'Systematic deduction from gross revenue, affects commercial margin.'
      },
      direction: 'down',
      impact: 'low'
    },
  ];

  // Executive reading bullets
  const executiveReading = [
    {
      pt: 'Royalties representam um encargo legal automático de 5% sobre o valor vendido.',
      en: 'Royalties represent an automatic legal charge of 5% on the sold value.'
    },
    {
      pt: 'As comissões de venda variam em função do tipo de venda, sendo de 2,0% para pedras regulares e 1,75% para pedras especiais em leilão.',
      en: 'Sales commissions vary according to the type of sale, being 2.0% for regular stones and 1.75% for special stones at auction.'
    },
    {
      pt: 'O acompanhamento desta rubrica permite medir o take rate comercial efectivo e o impacto directo na receita líquida e na capacidade de geração de caixa.',
      en: 'Monitoring this category allows measuring the effective commercial take rate and the direct impact on net revenue and cash generation capacity.'
    },
  ];

  return (
    <div className="space-y-6" data-testid="commercial-costs-analysis">
      {/* Header */}
      <div>
        {onBack && (
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
            style={{ color: '#7A859D' }}
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'pt' ? 'Voltar a Custos Operacionais' : 'Back to Operating Costs'}
          </button>
        )}
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Custos de Comercialização' : 'Commercial Costs'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights - Mensagens-Chave (same style as CMVMC) */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {insights.map((insight, idx) => {
            const driverName = typeof insight.driver === 'object' ? insight.driver[language] : insight.driver;
            const detail = typeof insight.detail === 'object' ? insight.detail[language] : insight.detail;
            const isUp = insight.direction === 'up';
            const isNeutral = insight.direction === 'neutral';
            const impactBg = insight.impact === 'high' ? 'bg-red-500/20' : insight.impact === 'medium' ? 'bg-amber-500/20' : 'bg-blue-500/20';
            const impactBorder = insight.impact === 'high' ? 'border-red-500/30' : insight.impact === 'medium' ? 'border-amber-500/30' : 'border-blue-500/30';
            
            return (
              <div 
                key={idx} 
                className={`${impactBg} ${impactBorder} border rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isNeutral ? 'bg-amber-500/30' : isUp ? 'bg-red-500/30' : 'bg-emerald-500/30'
                  }`}>
                    {isNeutral ? (
                      <Percent className="w-5 h-5 text-amber-400" />
                    ) : isUp ? (
                      <TrendingUp className="w-5 h-5 text-red-400" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white text-sm mb-1.5 leading-tight">{driverName}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPIs - same style as CMVMC with arrows */}
      <div className="grid grid-cols-5 gap-4">
        {/* KPI 1 - Custos Comercialização */}
        {(() => {
          const numChange = parseFloat(totalCostsYoY) || 0;
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositive ? '#10B981' : '#EF4444';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100 mb-3">
                <DollarSign className="w-5 h-5 text-primary" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Custos Comercialização' : 'Commercial Costs'}</p>
              <p className="font-serif text-2xl font-semibold text-primary">{(commercialData.totalCommercialCosts || 0).toFixed(1)}M<span className="text-sm text-slate-400 ml-1">USD</span></p>
              <div className="flex flex-wrap gap-1 mt-2">
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(commercialVsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {parseFloat(commercialVsBudget) > 0 ? '+' : ''}{commercialVsBudget}% Orç.
                </span>
                {totalCostsYoY && (
                  <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(totalCostsYoY) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {parseFloat(totalCostsYoY) > 0 ? '+' : ''}{totalCostsYoY}% YoY
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* KPI 2 - Peso nos Custos Operacionais */}
        {(() => {
          const numChange = parseFloat(weightInOpexChange) || 0;
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-amber-50';
          const trendColor = isPositive ? '#10B981' : '#F59E0B';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100 mb-3">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Peso nos Custos Operacionais' : 'Weight in Operating Costs'}</p>
              <p className="font-serif text-2xl font-semibold text-primary">{weightInOpex}%</p>
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-amber-50 text-amber-700">+0.2p.p. vs Orç.</span>
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(weightInOpexChange) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {parseFloat(weightInOpexChange) > 0 ? '+' : ''}{weightInOpexChange}p.p. YoY
                </span>
              </div>
            </div>
          );
        })()}

        {/* KPI 3 - % das Vendas (Take Rate) */}
        {(() => {
          const numChange = parseFloat(takeRateYoY) || 0;
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositive ? '#10B981' : '#EF4444';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100 mb-3">
                <Percent className="w-5 h-5 text-primary" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? '% das Vendas' : '% of Sales'}</p>
              <p className="font-serif text-2xl font-semibold text-primary">{commercialData.takeRatePercent || 0}%</p>
              <div className="flex flex-wrap gap-1 mt-2">
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(takeRateVsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {parseFloat(takeRateVsBudget) > 0 ? '+' : ''}{takeRateVsBudget}% Orç.
                </span>
                {takeRateYoY && (
                  <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(takeRateYoY) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {parseFloat(takeRateYoY) > 0 ? '+' : ''}{takeRateYoY}% YoY
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* KPI 4 - Mês Maior Custo Royalties */}
        {(() => {
          const numChange = parseFloat(peakRoyaltiesYoY) || 0;
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositive ? '#10B981' : '#EF4444';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#0A192F] mb-3">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Mês Maior Custo Royalties' : 'Peak Royalties Month'}</p>
              <p className="font-serif text-xl font-semibold text-primary">{peakRoyaltiesMonth}</p>
              <p className="text-sm text-slate-600 font-medium">{peakRoyaltiesValue}M USD</p>
              <div className="flex flex-wrap gap-1 mt-1">
                <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700">{peakRoyaltiesVsBudget}% Orç.</span>
                <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-amber-50 text-amber-700">{peakRoyaltiesYoY}% YoY</span>
              </div>
            </div>
          );
        })()}

        {/* KPI 5 - Mês Maior Custo Comissão de Vendas */}
        {(() => {
          const numChange = parseFloat(peakCommissionYoY) || 0;
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositive ? '#10B981' : '#EF4444';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#C5A059] mb-3">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Mês Maior Custo Comissão' : 'Peak Commission Month'}</p>
              <p className="font-serif text-xl font-semibold text-primary">{peakCommissionMonth}</p>
              <p className="text-sm text-slate-600 font-medium">{peakCommissionValue}M USD</p>
              <div className="flex flex-wrap gap-1 mt-1">
                <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700">{peakCommissionVsBudget}% Orç.</span>
                <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-amber-50 text-amber-700">{peakCommissionYoY}% YoY</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Resumo Executivo - Executive Summary (Same style as CMVMC) */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Custos de comercialização' : 'Commercial costs'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="commercial-costs-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? 'Em 2025, os custos de comercialização devem ser lidos sobretudo como uma rubrica de conversão da receita bruta em receita líquida, e não como uma linha clássica de eficiência operacional. A sua formação é maioritariamente mecânica: os royalties representam um encargo legal de 5% sobre o valor vendido, enquanto as comissões de venda variam em função do tipo de venda, sendo de 2,0% para pedras regulares e 1,75% para pedras especiais em leilão. Em termos de gestão, isto significa que o principal eixo de análise não é apenas o valor absoluto do custo, mas o take rate comercial efectivo e a sua variação em função do mix de receita.'
              : 'In 2025, commercial costs should be read mainly as a gross-to-net revenue conversion item, not as a classic operational efficiency line. Their formation is largely mechanical: royalties represent a legal charge of 5% on sold value, while sales commissions vary by sale type, being 2.0% for regular stones and 1.75% for special stones at auction. For management, this means the main analysis axis is not just the absolute cost value, but the effective commercial take rate and its variation based on revenue mix.'}
          </p>
        </div>

        {/* Pontos-chave - Key Highlights */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h4 className="font-semibold text-primary text-sm mb-4">{language === 'pt' ? 'Pontos-Chave' : 'Key Highlights'}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Natureza da rubrica */}
            <div className="bg-gradient-to-r from-slate-50 to-white rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Percent className="w-4 h-4 text-primary" />
                </div>
                <p className="text-sm font-medium text-primary">{language === 'pt' ? 'Natureza da Rubrica' : 'Item Nature'}</p>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {language === 'pt' 
                  ? 'Custo de comercialização com componente maioritariamente mecânica, ligada ao valor vendido e ao tipo de venda.'
                  : 'Commercial cost with largely mechanical component, linked to sold value and sale type.'}
              </p>
            </div>

            {/* Mês de maior custo */}
            <div className="bg-gradient-to-r from-amber-50 to-white rounded-xl p-4 border border-amber-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-sm font-medium text-primary">{language === 'pt' ? 'Mês de Maior Custo' : 'Peak Cost Month'}</p>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {language === 'pt' 
                  ? `${peakRoyaltiesMonth}, com USD ${peakRoyaltiesValue}M, explicado por maior receita e maior peso de regulares.`
                  : `${peakRoyaltiesMonth}, with USD ${peakRoyaltiesValue}M, explained by higher revenue and higher regular stones weight.`}
              </p>
            </div>

            {/* Comparação vs Orçamento */}
            <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Target className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-sm font-medium text-primary">{language === 'pt' ? 'vs Orçamento 2025' : 'vs Budget 2025'}</p>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {language === 'pt' 
                  ? `${parseFloat(commercialVsBudget) > 0 ? '+' : ''}${commercialVsBudget}% face ao orçamento, explicada por desvio da receita e mix de vendas.`
                  : `${parseFloat(commercialVsBudget) > 0 ? '+' : ''}${commercialVsBudget}% budget, explained by revenue deviation and sales mix.`}
              </p>
            </div>

            {/* Comparação vs período homólogo */}
            <div className="bg-gradient-to-r from-emerald-50 to-white rounded-xl p-4 border border-emerald-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-sm font-medium text-primary">{language === 'pt' ? 'vs Período Homólogo' : 'vs Prior Year'}</p>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {language === 'pt' 
                  ? `${totalCostsYoY ? (parseFloat(totalCostsYoY) > 0 ? '+' : '') + totalCostsYoY : '—'}% YoY, em linha com a variação da receita bruta.`
                  : `${totalCostsYoY ? (parseFloat(totalCostsYoY) > 0 ? '+' : '') + totalCostsYoY : '—'}% YoY, in line with gross revenue variation.`}
              </p>
            </div>
          </div>

          {/* Métrica principal de gestão */}
          <div className="mt-4 bg-gradient-to-r from-primary/5 to-transparent rounded-xl p-4 border border-primary/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Percent className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-primary">{language === 'pt' ? 'Métrica Principal de Gestão' : 'Key Management Metric'}</p>
                <p className="text-xs text-slate-600">
                  {language === 'pt' 
                    ? `Take rate comercial efectivo (${commercialData.takeRatePercent}%), para medir o impacto da rubrica na receita líquida e na geração de caixa.`
                    : `Effective commercial take rate (${commercialData.takeRatePercent}%), to measure the item's impact on net revenue and cash generation.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* Cost Structure with toggle buttons */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary">
            {language === 'pt' ? 'Estrutura dos Custos de Comercialização' : 'Commercial Costs Structure'}
          </h3>
          {/* Toggle buttons for comparison period */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setStructureCompareMode('yoy')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                structureCompareMode === 'yoy'
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              vs {yearNum - 1}
            </button>
            <button
              onClick={() => setStructureCompareMode('budget')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                structureCompareMode === 'budget'
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {language === 'pt' ? 'vs Orçamento 2025' : 'vs Budget 2025'}
            </button>
          </div>
        </div>
        
        {/* Main Bar Container */}
        <div className="flex items-center gap-6">
          {/* Stacked Bar */}
          <div className="flex-1">
            {/* Labels above bar with comparison */}
            <div className="flex justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#0A192F]"></div>
                <span className="text-sm font-medium text-slate-700">Royalties (5%)</span>
                {structureCompareMode === 'yoy' ? (
                  <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(royaltiesYoY) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {parseFloat(royaltiesYoY) > 0 ? '+' : ''}{royaltiesYoY}% vs {yearNum - 1}
                  </span>
                ) : (
                  <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700">
                    +3.1% Orç.
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#C5A059]"></div>
                <span className="text-sm font-medium text-slate-700">
                  {language === 'pt' ? 'Comissão de Vendas' : 'Sales Commission'}
                </span>
                {structureCompareMode === 'yoy' ? (
                  <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(sodiamYoY) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {parseFloat(sodiamYoY) > 0 ? '+' : ''}{sodiamYoY}% vs {yearNum - 1}
                  </span>
                ) : (
                  <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700">
                    +4.2% Orç.
                  </span>
                )}
              </div>
            </div>
            
            {/* The Bar */}
            <div className="relative h-14 rounded-lg overflow-hidden flex shadow-inner">
              {/* Royalties segment */}
              <div 
                className="bg-[#0A192F] h-full flex items-center justify-center relative group transition-all duration-300 hover:brightness-110"
                style={{ width: `${costStructureData[0]?.percentage || 72}%` }}
              >
                <span className="text-white font-bold text-lg">{costStructureData[0]?.percentage || 72}%</span>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="bg-slate-800 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap text-sm">
                    <p className="font-semibold">Royalties</p>
                    <p>{(costStructureData[0]?.value || 0).toFixed(2)}M USD</p>
                    {structureCompareMode === 'yoy' && (
                      <p className="text-amber-300">{yearNum - 1}: {(costStructureData[0]?.prevValue || 0).toFixed(2)}M</p>
                    )}
                    {structureCompareMode === 'budget' && (
                      <p className="text-blue-300">Orç.: {(costStructureData[0]?.budgetValue || 0).toFixed(2)}M</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Sales Commission segment */}
              <div 
                className="bg-[#C5A059] h-full flex items-center justify-center relative group transition-all duration-300 hover:brightness-110"
                style={{ width: `${costStructureData[1]?.percentage || 28}%` }}
              >
                <span className="text-white font-bold text-lg">{costStructureData[1]?.percentage || 28}%</span>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="bg-slate-800 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap text-sm">
                    <p className="font-semibold">{language === 'pt' ? 'Comissão de Vendas' : 'Sales Commission'}</p>
                    <p>{(costStructureData[1]?.value || 0).toFixed(2)}M USD</p>
                    {structureCompareMode === 'yoy' && (
                      <p className="text-amber-300">{yearNum - 1}: {(costStructureData[1]?.prevValue || 0).toFixed(2)}M</p>
                    )}
                    {structureCompareMode === 'budget' && (
                      <p className="text-blue-300">Orç.: {(costStructureData[1]?.budgetValue || 0).toFixed(2)}M</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Values below bar with comparison */}
            <div className="flex justify-between mt-2 text-sm">
              <div>
                <span className="text-slate-700 font-semibold">{(costStructureData[0]?.value || 0).toFixed(2)}M USD</span>
                <span className="text-slate-400 ml-2">
                  {structureCompareMode === 'yoy' 
                    ? `(${yearNum - 1}: ${(costStructureData[0]?.prevValue || 0).toFixed(2)}M)` 
                    : `(Orç.: ${(costStructureData[0]?.budgetValue || 0).toFixed(2)}M)`}
                </span>
              </div>
              <div>
                <span className="text-slate-700 font-semibold">{(costStructureData[1]?.value || 0).toFixed(2)}M USD</span>
                <span className="text-slate-400 ml-2">
                  {structureCompareMode === 'yoy' 
                    ? `(${yearNum - 1}: ${(costStructureData[1]?.prevValue || 0).toFixed(2)}M)` 
                    : `(Orç.: ${(costStructureData[1]?.budgetValue || 0).toFixed(2)}M)`}
                </span>
              </div>
            </div>
          </div>
          
          {/* Take Rate Total - Right Side */}
          <div className="flex-shrink-0 w-36 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Take Rate</p>
            <p className="font-serif text-3xl font-bold text-primary">{commercialData.takeRatePercent || 0}%</p>
            <p className="text-xs text-slate-400 mt-1">{language === 'pt' ? 'das vendas' : 'of sales'}</p>
          </div>
        </div>
        
        {/* Summary Row with Comparatives */}
        <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Total Custos */}
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{language === 'pt' ? 'Total Custos' : 'Total Costs'}</p>
              <p className="font-serif text-xl font-bold text-primary">{(commercialData.totalCommercialCosts || 0).toFixed(2)}M USD</p>
              <div className="flex items-center gap-1.5 mt-1">
                {structureCompareMode === 'yoy' ? (
                  <>
                    <span className="text-xs text-slate-400">
                      {yearNum - 1}: {(prevCommercialData.totalCommercialCosts || 0).toFixed(2)}M
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(totalCostsYoY) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {parseFloat(totalCostsYoY) > 0 ? '+' : ''}{totalCostsYoY}%
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-slate-400">
                      Orç.: {budgetCommercialCosts.toFixed(2)}M
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(commercialVsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {parseFloat(commercialVsBudget) > 0 ? '+' : ''}{commercialVsBudget}%
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="h-12 w-px bg-slate-200"></div>
            {/* Vendas Brutas */}
            <div>
              <p className="text-xs text-slate-500 mb-0.5">{language === 'pt' ? 'Vendas Brutas' : 'Gross Sales'}</p>
              <p className="font-serif text-xl font-bold text-slate-600">{(commercialData.grossSales || 0).toFixed(1)}M USD</p>
              <div className="flex items-center gap-1.5 mt-1">
                {structureCompareMode === 'yoy' ? (
                  <>
                    <span className="text-xs text-slate-400">
                      {yearNum - 1}: {(prevCommercialData.grossSales || 0).toFixed(1)}M
                    </span>
                    {prevCommercialData.grossSales && (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${((commercialData.grossSales - prevCommercialData.grossSales) / prevCommercialData.grossSales * 100) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {((commercialData.grossSales - prevCommercialData.grossSales) / prevCommercialData.grossSales * 100) >= 0 ? '+' : ''}{((commercialData.grossSales - prevCommercialData.grossSales) / prevCommercialData.grossSales * 100).toFixed(1)}%
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-xs text-slate-400">
                      Orç.: {(commercialData.grossSales * 0.97).toFixed(1)}M
                    </span>
                    <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-emerald-100 text-emerald-700">
                      +3.1%
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Receita Líquida */}
          <div className="text-right">
            <p className="text-xs text-slate-500 mb-0.5">{language === 'pt' ? 'Receita Líquida após Encargos' : 'Net Revenue after Charges'}</p>
            <p className="font-serif text-xl font-bold text-emerald-600">{(commercialData.netRevenueAfterCharges || 0).toFixed(1)}M USD</p>
            <div className="flex items-center justify-end gap-1.5 mt-1">
              {structureCompareMode === 'yoy' ? (
                <>
                  <span className="text-xs text-slate-400">
                    {yearNum - 1}: {(prevCommercialData.netRevenueAfterCharges || 0).toFixed(1)}M
                  </span>
                  {prevCommercialData.netRevenueAfterCharges && (
                    <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${((commercialData.netRevenueAfterCharges - prevCommercialData.netRevenueAfterCharges) / prevCommercialData.netRevenueAfterCharges * 100) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {((commercialData.netRevenueAfterCharges - prevCommercialData.netRevenueAfterCharges) / prevCommercialData.netRevenueAfterCharges * 100) >= 0 ? '+' : ''}{((commercialData.netRevenueAfterCharges - prevCommercialData.netRevenueAfterCharges) / prevCommercialData.netRevenueAfterCharges * 100).toFixed(1)}%
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="text-xs text-slate-400">
                    Orç.: {(commercialData.netRevenueAfterCharges * 0.98).toFixed(1)}M
                  </span>
                  <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-emerald-100 text-emerald-700">
                    +2.0%
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialCostsAnalysis;
